import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { ApPermissionService } from '../services/ap-permission.service';
import { ExpensePermissionService } from '../services/expense-permission.service';
import { ContextService } from '../services/context.service';
import { AiPreferenceService } from '../services/ai-preference.service';
import { ImpersonationService } from '../services/impersonation.service';
import { CompanyModuleName } from '../models/company-minimal-model';
import { RoleConstant } from '../models/role-constant';
import { ToasterService } from '../utils/toaster.service';
import { AccountsPayableAction } from '../models/accounts-payable-actions.model';
import { ExpenseAction } from '../models/expense-actions.model';
import { RmAction } from '../models/rm-actions.model';
import { RmPermissionService } from '../services/rm-permission.service';
import { isTemporaryDashboardOverviewNavigation } from '../pages/dashboard/overview/data/widget-drilldown.util';
import {
  isAccountsPayableUrlAllowed,
  isSidebarModuleAllowedForRole,
  resolveSidebarRouteModule,
} from '../utils/sidebar-route-access.util';
import {
  SIDEBAR_MODULE_PAGE_URLS,
  resolveBePageUrlAllowed,
  resolveSidebarPageUrlAllowed,
} from '../utils/company-permission.util';
import {
  getInviteIdFromUrl,
  isExternalPaymentDetailsInvite,
  isVendorCreateRoute,
} from '../utils/invite-id.util';

/** Route prefixes of each organization module. */
const MODULE_ROUTES: readonly { prefix: string; module: CompanyModuleName }[] = [
  { prefix: 'accounts-payable', module: 'AccountsPayable' },
  { prefix: 'expenses', module: 'ExpenseManagement' },
  { prefix: 'repairs-and-maintenance', module: 'RepairAndMaintenance' },
];

function routeStartsWith(url: string, prefix: string): boolean {
  const path = (url || '').split(/[?#]/)[0].replace(/^\/+/, '').toLowerCase();
  return path === prefix || path.startsWith(prefix + '/');
}

/**
 * Blocks deep-links / typed URLs that are outside the current role's sidebar
 * (and AP pagePermissions). Complements PermissionGuard CRUD checks.
 */
@Injectable({ providedIn: 'root' })
export class RouteAccessGuard implements CanActivateChild {
  constructor(
    private contextSvc: ContextService,
    private apPermissionSvc: ApPermissionService,
    private expensePermissionSvc: ExpensePermissionService,
    private rmPermissionSvc: RmPermissionService,
    private aiPrefSvc: AiPreferenceService,
    private impersonationSvc: ImpersonationService,
    private toasterSvc: ToasterService,
    private router: Router
  ) {}

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return of(this.evaluate(state.url, childRoute));
  }

  private evaluate(
    url: string,
    route: ActivatedRouteSnapshot
  ): boolean | UrlTree {
    if (isExternalPaymentDetailsInvite(url, route.queryParams, route.queryParamMap)) {
      return true;
    }

    const inviteId = getInviteIdFromUrl(url);
    if (inviteId && isVendorCreateRoute(url)) {
      return true;
    }
    if (url.includes('/asset-management/new-asset')) {
      return true;
    }

    // Temporary: dashboard widget drilldown → /…/overview?dashboardRedirect=1
    if (isTemporaryDashboardOverviewNavigation(url)) {
      return true;
    }

    // Modules switched off for the organization can't be opened by URL either.
    const inactiveModule = MODULE_ROUTES.find(
      (x) => routeStartsWith(url, x.prefix) && !this.contextSvc.isCompanyModuleActive(x.module)
    );
    if (inactiveModule) {
      this.toasterSvc.showInfo('This module is not active for your organization.');
      return this.router.createUrlTree(['/']);
    }

    // AC 7.4: a System Admin inside an organization only gets organization-level features.
    if (!this.impersonationSvc.isRouteAllowed(url)) {
      this.toasterSvc.showInfo('This area is not available while you are inside an organization.');
      return this.router.createUrlTree(['/']);
    }

    // …and in the portal (not inside an organization) none of them, even by typed URL.
    if (
      this.contextSvc.isGlobalAdmin() &&
      !this.impersonationSvc.isActive &&
      !this.impersonationSvc.isPortalRouteAllowed(url)
    ) {
      this.toasterSvc.showInfo('Enter an organization to open this area.');
      return this.router.createUrlTree(['/']);
    }

    const roleId =
      this.contextSvc.selectedRoleId$.value ??
      (typeof localStorage !== 'undefined'
        ? localStorage.getItem('selectedRoleId')
        : null);

    // The Mealy chat route itself — not AI Preferences, which must stay reachable so a user who
    // turned AI off can turn it back on, and not the rest of the "shell" allowlist below, which
    // covers unrelated always-open pages (profile, notifications, support).
    if (url.split('?')[0].replace(/^\/+/, '').startsWith('meal-dynamics-ai') && !this.aiPrefSvc.allowed) {
      this.toasterSvc.showInfo('AI features are turned off in your AI Preferences.');
      return this.router.createUrlTree(['/']);
    }

    const module = resolveSidebarRouteModule(url);

    if (module === 'shell') {
      return true;
    }

    if (module === 'ap') {
      return this.evaluateAccountsPayable(url);
    }

    if (module === 'expense') {
      return this.evaluateExpense(url);
    }

    // BE pagePermissions + Organization actionPermissions only, no role allowlist.
    if (module === 'integrationData') {
      return this.contextSvc.canOpenIntegrationData() ? true : this.denied();
    }
    if (module === 'organizationSettings') {
      return this.contextSvc.canOpenOrganizationSettings() ? true : this.denied();
    }

    // Approval Process pages are reachable via the R&M "Approval process" tab when BE grants the actions.
    if (
      module === 'approvalProcess' &&
      this.rmPermissionSvc.canAny([
        RmAction.CreateApprovalProcess,
        RmAction.UpdateApprovalProcess,
        RmAction.DeleteApprovalProcess,
      ])
    ) {
      return true;
    }

    // The System Admin always manages organizations, whatever its pagePermissions list.
    if (module === 'company' && roleId === RoleConstant.globalAdminId) {
      return true;
    }

    const modulePageUrl = SIDEBAR_MODULE_PAGE_URLS[module];
    if (modulePageUrl) {
      const legacyAllowed = isSidebarModuleAllowedForRole(module, roleId);
      // Use active company (role company → selected) so lookup matches sidebar.
      // Organizations is BE-only (no role fallback when /my sends pagePermissions); the rest keep the fallback.
      const resolve = module === 'company' ? resolveBePageUrlAllowed : resolveSidebarPageUrlAllowed;
      const allowed = resolve(
        this.contextSvc.getStoredPermissionsForRole(roleId),
        this.contextSvc.getActiveCompanyId(),
        modulePageUrl,
        legacyAllowed
      );
      return allowed ? true : this.denied();
    }

    if (!isSidebarModuleAllowedForRole(module, roleId)) {
      return this.denied();
    }

    return true;
  }

  private evaluateExpense(url: string): boolean | UrlTree {
    if (
      !this.expensePermissionSvc.hasSidebar() &&
      !this.expensePermissionSvc.canView()
    ) {
      return this.denied();
    }

    const path = url.split('?')[0].toLowerCase();

    if (path.includes('/expenses/new')) {
      return this.expensePermissionSvc.can(ExpenseAction.Create)
        ? true
        : this.denied();
    }

    if (path.includes('/expenses/edit')) {
      return this.expensePermissionSvc.canEditRoute() ? true : this.denied();
    }

    return this.expensePermissionSvc.canView() ? true : this.denied();
  }

  private evaluateAccountsPayable(url: string): boolean | UrlTree {
    const subMenus = this.apPermissionSvc.getAllSubMenus();
    if (!subMenus.length && !this.apPermissionSvc.canCreate()) {
      return this.denied();
    }

    const allowed = isAccountsPayableUrlAllowed(url, subMenus, {
      allowCreate: this.apPermissionSvc.can(AccountsPayableAction.Create),
      allowCollatedPayment: this.apPermissionSvc.canAccessCollatedPayment(),
    });

    return allowed ? true : this.denied();
  }

  private denied(): UrlTree {
    return this.router.createUrlTree(['/auth/error']);
  }

}
